import React from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import Button from '../UIKit/Button';

/**
 * Компонент подсказки для отображения шага туториала
 *
 * @param {Object} props - свойства компонента
 * @param {string|Object} props.title - заголовок шага (строка или объект с локализациями)
 * @param {string|Object} props.content - содержимое шага (строка или объект с локализациями)
 * @param {Object} props.position - позиция подсказки
 * @param {string} props.placement - расположение подсказки
 * @param {function} props.onNext - обработчик нажатия кнопки "Далее"
 * @param {function} props.onPrev - обработчик нажатия кнопки "Назад"
 * @param {function} props.onSkip - обработчик нажатия кнопки "Пропустить"
 * @param {boolean} props.showPrev - показывать ли кнопку "Назад"
 * @param {boolean} props.showNext - показывать ли кнопку "Далее"
 * @param {boolean} props.isLastStep - последний ли это шаг
 * @param {number} props.currentStep - текущий шаг
 * @param {number} props.totalSteps - общее количество шагов
 */
const Tooltip = ({
  title,
  content,
  position,
  placement = 'bottom',
  onNext,
  onPrev,
  onSkip,
  showPrev = true,
  showNext = true,
  isLastStep = false,
  currentStep = 0,
  totalSteps = 1,
}) => {
  const { t, i18n } = useTranslation(['onboarding', 'common']);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // --- Локализация title/content ---
  const lang = i18n.language || 'en';
  
  // Функция для получения локализованного значения из строки или объекта
  const getLocalized = (val) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    return val[lang] || val['en'] || val['ru'] || '';
  };
  
  const localizedTitle = getLocalized(title);
  const localizedContent = getLocalized(content);

  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Обработчики кнопок
  const handleNext = () => {
    onNext && onNext();
  };

  const handlePrev = () => {
    onPrev && onPrev();
  };

  const handleSkip = () => {
    onSkip && onSkip();
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: 'calc(100vw - 40px)',
        p: 0.5,
        zIndex: 10000,
        pointerEvents: 'auto',
        animation: 'slideIn 0.3s ease-out',
        display: 'flex',
        flexDirection: 'column',
        '@keyframes slideIn': {
          from: {
            opacity: 0,
            transform: 'translateY(10px)',
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
      }}
    >
      <Box mb={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              // На мобильных уменьшаем размер шрифта
              fontSize: { xs: '1rem', sm: '1.25rem' },
            }}
          >
            {localizedTitle}
          </Typography>
        </Box>
      </Box>

      <Box mb={1}>
        <Typography variant="caption" color="text.secondary">
          {t('onboarding:progress', {
            current: currentStep + 1,
            total: totalSteps,
          })}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 4,
            borderRadius: 2,
            mt: 0.5,
            backgroundColor: 'rgba(74, 144, 226, 0.2)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: 'primary.main',
            },
          }}
        />
      </Box>

      <Box
        mb={2}
        sx={{
          maxHeight: { xs: 'auto', sm: 'auto' },
          overflowY: 'visible',
          lineHeight: 1.4,
          flex: '1 1 auto',
          // Стилизуем скроллбар
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.05)',
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.15)',
            borderRadius: 3,
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.25)',
            },
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: 'text.primary',
            fontSize: '0.9rem',
            lineHeight: 1.5,
          }}
        >
          {localizedContent}
        </Typography>
      </Box>

      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobile ? 'stretch' : 'center'}
        spacing={isMobile ? 1 : 0}
        mt={1}
      >
        {!isMobile && (
          <Button size="small" variant="text" onClick={handleSkip}>
            {t('onboarding:buttons.skip')}
          </Button>
        )}

        <Stack
          direction="row"
          spacing={1}
          sx={{
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'space-between' : 'flex-end',
          }}
        >
          {showPrev && (
            <Button
              size="small"
              variant="outlined"
              onClick={handlePrev}
              startIcon={<ArrowBackIcon />}
              sx={{ width: isMobile ? '100%' : 'auto' }}
            >
              {t('onboarding:buttons.prev')}
            </Button>
          )}
          {showNext && (
            <Button
              size="small"
              variant="contained"
              onClick={handleNext}
              endIcon={!isLastStep && <ArrowForwardIcon />}
              sx={{ width: isMobile ? '100%' : 'auto' }}
            >
              {isLastStep
                ? t('onboarding:buttons.finish')
                : t('onboarding:buttons.next')}
            </Button>
          )}
        </Stack>

        {isMobile && (
          <Button
            size="small"
            variant="text"
            sx={{ width: '100%', mt: 1 }}
            onClick={handleSkip}
          >
            {t('onboarding:buttons.skip')}
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default Tooltip;
